import * as React from "react";
import { observer } from "mobx-react-lite";
import { mk8sDryRun } from "../../../../mobxStores/dryRun/mk8s";
import { Mk8sDraftMobx } from "../../../../mst/stores/mk8s.draft";
import { Mk8sDraftProviderEphemeralMobx } from "../../../../mst/stores/mk8s.draft.provider.ephemeral";
import { DryRunAlert } from "../../dryRunAlert";
import { useLocation, useNavigate } from "react-router-dom";
import { NGInput } from "../../../../newcomponents/input/input";
import { NGSelect } from "../../../../newcomponents/select/ngselect";
import { NGButton } from "../../../../newcomponents/button/Button";
import { mk8sMobx } from "../../../../mst/kinds/mk8s/mk8s";
import { NGInputListMst } from "../../../../newcomponents/inputList/inputListMst";
import { NGFormElement } from "../../../../newcomponents/ngformelement/ngformelement";
import { getDryRunErrorText } from "../../getDryRunErrorText";
import { DetectedTags, useHasDetectedTags } from "../../../../components/detail/detectedTags";
import { Instance } from "mobx-state-tree";
import { Mk8sDraftProviderEphemeralNodePoolsModel } from "../../../../mst/stores/mk8s.draft.provider.ephemeral.nodePool";

interface Props {
  mk8s: mk8sMobx;
  mk8sDraft: Mk8sDraftMobx;
  ephemeral: Mk8sDraftProviderEphemeralMobx;
  nodePool: Instance<typeof Mk8sDraftProviderEphemeralNodePoolsModel>;
  index: number;
}
const ProviderEphemeralNodePoolRaw: React.FC<Props> = ({ ephemeral: e, nodePool: np, index }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const hasDetectedTags = useHasDetectedTags({
    value: np.labels.items,
    detectValue: (labels, key) => labels.some((l) => l.firstValue === key),
    detectionMap: {
      "cpln.io/nodeType": "if value is core: nodes can accept BYOK core components",
    },
  });

  const pre = `ephemeral.nodePools[${index}].`;

  const labelsDryRun = getDryRunErrorText({
    dryRunResponse: mk8sDryRun.response,
    path: `spec.provider.ephemeral.nodePools[${index}].labels`,
    paths: np.labels.items.map((i) => `spec.provider.ephemeral.nodePools[${index}].labels.${i.firstValue}`),
  });
  const taintsDryRun = getDryRunErrorText({
    dryRunResponse: mk8sDryRun.response,
    path: `spec.provider.ephemeral.nodePools[${index}].taints`,
    paths: [
      ...np.taints.items.map((_, idx) => `spec.provider.ephemeral.nodePools[${index}].taints[${idx}]`),
      ...np.taints.items.map((_, idx) => `spec.provider.ephemeral.nodePools[${index}].taints[${idx}].key`),
      ...np.taints.items.map((_, idx) => `spec.provider.ephemeral.nodePools[${index}].taints[${idx}].value`),
      ...np.taints.items.map((_, idx) => `spec.provider.ephemeral.nodePools[${index}].taints[${idx}].effect`),
    ],
  });

  return (
    <>
      <div>
        <NGFormElement
          name={`${pre}name`}
          label={np.name.label}
          value={np.name.value}
          onChange={np.name.setValue}
          required
          error={!e.isNodePoolNameValid(np.index) ? "Node pool names must be unique" : ""}
        />

        <div className="flex gap-2 items-start">
          <NGFormElement
            name={`${pre}count`}
            label={np.count.label}
            value={np.count.value}
            onChange={np.count.setValue}
            required={np.count.isRequired}
            error={np.count.error}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={true}
              dryRunResponse={mk8sDryRun.response}
              onFix={np.count.setValue}
              path={`spec.provider.ephemeral.nodePools[${index}].count`}
            />
          </div>
        </div>
        <div className="flex gap-2 items-start">
          <NGFormElement
            name={`${pre}arch`}
            as={"select"}
            label={np.arch.label}
            options={np.arch.options}
            value={np.arch.value}
            required
            onChange={np.arch.setValue}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={true}
              dryRunResponse={mk8sDryRun.response}
              onFix={np.arch.setValue}
              path={`spec.provider.ephemeral.nodePools[${index}].arch`}
            />
          </div>
        </div>
        <div className="flex gap-2 items-start">
          <NGFormElement
            name={`${pre}flavor`}
            as={"select"}
            label={np.flavor.label}
            options={np.flavor.options}
            value={np.flavor.value}
            required
            onChange={np.flavor.setValue}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={true}
              dryRunResponse={mk8sDryRun.response}
              onFix={np.flavor.setValue}
              path={`spec.provider.ephemeral.nodePools[${index}].flavor`}
            />
          </div>
        </div>
        <div className="flex gap-2 items-start">
          <NGFormElement
            name={`${pre}cpu`}
            label={np.cpu.label}
            value={np.cpu.value}
            onChange={np.cpu.setValue}
            required={np.cpu.isRequired}
            error={np.cpu.error}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={true}
              dryRunResponse={mk8sDryRun.response}
              onFix={np.cpu.setValue}
              path={`spec.provider.ephemeral.nodePools[${index}].cpu`}
            />
          </div>
        </div>
        <div className="flex gap-2 items-start">
          <NGFormElement
            name={`${pre}memory`}
            label={np.memory.label}
            value={np.memory.value}
            onChange={np.memory.setValue}
            required={np.memory.isRequired}
            error={np.memory.error}
          />
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={true}
              dryRunResponse={mk8sDryRun.response}
              onFix={np.memory.setValue}
              path={`spec.provider.ephemeral.nodePools[${index}].memory`}
            />
          </div>
        </div>
        <NGInputListMst
          data={np.labels}
          className={hasDetectedTags ? "mb-2" : "mb-8"}
          label="Labels"
          labelRender={() => {
            return (
              <>
                {np.labels.items.some((l) => l.firstValue === "cpln.io/nodeType" && l.secondValue === "core") ? null : (
                  <NGButton
                    variant="primary"
                    className="ml-2"
                    onClick={() => {
                      const id = np.labels.add();
                      const item = np.labels.items.find((i) => i.id === id);
                      if (item) {
                        item.setFirstValue("cpln.io/nodeType");
                        item.setSecondValue("core");
                      }
                    }}
                    size={`small`}
                  >
                    Use this nodepool as BYOK core
                  </NGButton>
                )}
              </>
            );
          }}
          firstLabel="Key"
          firstInput={(i) => {
            let invalid = false;
            if (!i.firstValue) {
              invalid = true;
            }
            if (i.firstValue && labelsDryRun.message.includes(`.${i.firstValue}`)) {
              invalid = true;
            }

            return (
              <NGInput
                required
                invalid={invalid}
                value={i.firstValue}
                onChange={(e) => i.setFirstValue(e.target.value)}
              />
            );
          }}
          secondLabel="Value"
          secondInput={(i) => {
            let invalid = false;
            if (!i.secondValue) {
              invalid = true;
            }

            return (
              <NGInput
                required
                invalid={invalid}
                value={i.secondValue}
                onChange={(e) => i.setSecondValue(e.target.value)}
              />
            );
          }}
          invalid={labelsDryRun.severity === "error"}
          error={labelsDryRun.severity === "error" ? labelsDryRun.message : ""}
          warning={labelsDryRun.severity === "error" ? "" : labelsDryRun.message}
        />
        <DetectedTags
          className="mb-8"
          value={np.labels.items}
          detectValue={(labels, key) => labels.some((l) => l.firstValue === key)}
          detectionMap={{
            "cpln.io/nodeType": "if value is core: nodes can accept BYOK core components",
          }}
          pluralLabel="Labels"
        />
        <NGInputListMst
          data={np.taints}
          className="mb-8"
          label="Taints"
          firstLabel="Key"
          firstInput={(i, index) => {
            let invalid = false;
            if (!i.firstValue) {
              invalid = true;
            }
            if (i.firstValue && taintsDryRun.message.includes(`[${index}].key`)) {
              invalid = true;
            }

            return (
              <NGInput
                required
                invalid={invalid}
                value={i.firstValue}
                onChange={(e) => i.setFirstValue(e.target.value)}
              />
            );
          }}
          secondLabel="Value"
          secondInput={(i) => {
            let invalid = false;
            if (!i.secondValue) {
              invalid = true;
            }
            if (i.secondValue && taintsDryRun.message.includes(`[${index}].value`)) {
              invalid = true;
            }

            return (
              <NGInput
                required
                invalid={invalid}
                value={i.secondValue}
                onChange={(e) => i.setSecondValue(e.target.value)}
              />
            );
          }}
          thirdLabel="Effect"
          thirdInput={(i) => {
            let invalid = false;
            if (i.thirdValue && taintsDryRun.message.includes(`[${index}].effect`)) {
              invalid = true;
            }

            return (
              <NGSelect
                invalid={invalid}
                value={i.thirdValue}
                onChange={(value) => i.setThirdValue(value)}
                options={[
                  { label: "NoSchedule", value: "NoSchedule" },
                  { label: "PreferNoSchedule", value: "PreferNoSchedule" },
                  { label: "NoExecute", value: "NoExecute" },
                ]}
              />
            );
          }}
          invalid={taintsDryRun.severity === "error"}
          error={taintsDryRun.severity === "error" ? taintsDryRun.message : ""}
          warning={taintsDryRun.severity === "error" ? "" : taintsDryRun.message}
        />
        <NGButton
          style={{ width: 220 }}
          variant={"danger"}
          outlined
          onClick={() => {
            e.removeNodePoolAt(np.index);
            navigate(`${pathname.split("/-node-pools")[0]}/-node-pools`);
          }}
        >
          Delete Node Pool
        </NGButton>
      </div>
    </>
  );
};

export const ProviderEphemeralNodePool = observer(ProviderEphemeralNodePoolRaw);
